// The "current round" is the game day the app shows in Tänään. NHL games are
// listed under their North American date, which is mostly the night after in
// Finland, so the round changes at a fixed time of the day in Helsinki, the same
// convention the backend uses for its game day: at 19:00 Helsinki time, or at
// the hour of that day's first game if that is earlier (a Global Series game in
// Europe, say). Before it the round is the previous night's games, from then on
// it is tonight's. The day is a UTC day and only the Helsinki offset comes from
// Intl, so every device agrees whatever its own time zone, and daylight saving
// needs no special handling.

const HELSINKI = 'Europe/Helsinki';
export const ROUND_CUTOFF_HOUR = 19;

const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: HELSINKI,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    hourCycle: 'h23',
});

interface HelsinkiTime {
    year: number;
    month: number;
    day: number;
    hour: number;
}

export function helsinkiTime(instant: Date): HelsinkiTime {
    const parts = Object.fromEntries(formatter.formatToParts(instant).map(part => [part.type, part.value]));
    return {
        year: Number(parts.year),
        month: Number(parts.month),
        day: Number(parts.day),
        hour: Number(parts.hour) % 24,
    };
}

const pad2 = (value: number) => String(value).padStart(2, '0');

export const toDateString = (year: number, month: number, day: number) =>
    `${year}-${pad2(month)}-${pad2(day)}`;

// Adds days to a "YYYY-MM-DD" date string (calendar arithmetic, no time zones).
export function addDays(date: string, days: number): string {
    const [year, month, day] = date.split('-').map(Number);
    const shifted = new Date(Date.UTC(year, month - 1, day + days));
    return toDateString(shifted.getUTCFullYear(), shifted.getUTCMonth() + 1, shifted.getUTCDate());
}

interface ScheduledGame {
    // The game's day as the NHL lists it ("YYYY-MM-DD").
    date: string;
    // ISO timestamp.
    startTime: string;
}

const utcDateKey = (instant: Date) => instant.toISOString().slice(0, 10);

// The UTC hour at which the game day switches on a UTC date, the same rule as the
// backend's getDayDividerHour(): 19:00 Helsinki time (16 UTC in summer, 17 UTC in
// winter), or the UTC hour of the date's first game if that starts earlier on the
// same UTC date. The usual North American evening games start on the next UTC
// date and do not count.
export function getDayDividerHour(date: string, games: readonly ScheduledGame[] = []): number {
    // Measure the offset mid-day so a daylight saving change (at night) does not affect it.
    const offset = helsinkiTime(new Date(`${date}T12:00:00Z`)).hour - 12;
    let hour = ROUND_CUTOFF_HOUR - offset;
    for (const game of games) {
        const start = new Date(game.startTime);
        if (game.date === date && !Number.isNaN(start.getTime()) && utcDateKey(start) === date) {
            hour = Math.min(hour, start.getUTCHours());
        }
    }
    return hour;
}

// The current round's game day as "YYYY-MM-DD". The schedule is only needed to
// know about games that start earlier than the usual switch.
export function getRoundDate(now: Date = new Date(), games: readonly ScheduledGame[] = []): string {
    const today = utcDateKey(now);
    return now.getUTCHours() >= getDayDividerHour(today, games) ? today : addDays(today, -1);
}

// 0 (Sunday) to 6 (Saturday) for a "YYYY-MM-DD" date.
export function weekdayOf(date: string): number {
    const [year, month, day] = date.split('-').map(Number);
    return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
}

const WEEKDAY_KEYS = [
    'day_sunday', 'day_monday', 'day_tuesday', 'day_wednesday', 'day_thursday', 'day_friday', 'day_saturday',
] as const;

// "Ti 18.11." / "Tue 18.11." -- weekday abbreviation, day and month.
export function formatDayLabel(date: string, t: (key: string) => string): string {
    const [, month, day] = date.split('-').map(Number);
    return `${t(WEEKDAY_KEYS[weekdayOf(date)])} ${day}.${month}.`;
}
